import React, { useMemo, useState } from 'react';
import type { OpsLogisticDefinition, OpsLogisticType } from '../../types';

function safeString(v: any): string {
  return String(v || '').trim();
}

const TYPE_LABEL: Record<OpsLogisticType, string> = {
  text: 'Text',
  number: 'Number',
  date: 'Date',
  date_list: 'Dates',
  time: 'Time',
  boolean: 'Yes/No',
  enum: 'Enum',
  url: 'URL',
  person: 'Person',
  contact_date: 'Contact + date',
  string_list: 'List',
  file: 'File',
  file_list: 'Files',
};

const TYPE_ORDER: OpsLogisticType[] = [
  'enum',
  'boolean',
  'person',
  'contact_date',
  'date',
  'date_list',
  'time',
  'number',
  'text',
  'url',
  'string_list',
  'file',
  'file_list',
];

export default function LogisticsMultiPickModal(props: {
  open: boolean;
  title?: string;
  logistics: ReadonlyArray<OpsLogisticDefinition>;
  selectedIds: string[];
  onChangeSelectedIds: (ids: string[]) => void;
  onClose: () => void;
  onConfirm: () => void;
}): React.ReactElement | null {
  const { open, logistics, selectedIds, onChangeSelectedIds, onClose, onConfirm } = props;
  const [query, setQuery] = useState('');

  const selected = useMemo(() => new Set(selectedIds.map(String)), [selectedIds]);

  const filtered = useMemo(() => {
    const needle = safeString(query).toLowerCase();
    if (!needle) return logistics.slice();
    return logistics.filter(l => {
      const id = String(l.id || '').toLowerCase();
      const label = String(l.label || '').toLowerCase();
      return id.includes(needle) || label.includes(needle);
    });
  }, [logistics, query]);

  const grouped = useMemo(() => {
    const m = new Map<OpsLogisticType, OpsLogisticDefinition[]>();
    for (const l of filtered) {
      const t = (l.type || 'text') as OpsLogisticType;
      const arr = m.get(t) || [];
      arr.push(l);
      m.set(t, arr);
    }
    for (const [k, arr] of m.entries()) {
      arr.sort((a, b) => String(a.label || a.id).localeCompare(String(b.label || b.id)));
      m.set(k, arr);
    }
    return m;
  }, [filtered]);

  if (!open) return null;

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChangeSelectedIds(Array.from(next));
  };

  const clear = () => onChangeSelectedIds([]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: 'min(980px, 100%)',
          maxHeight: 'min(80vh, 720px)',
          overflow: 'hidden',
          borderRadius: 14,
          background: 'white',
          border: '1px solid #e5e7eb',
          boxShadow: '0 18px 45px rgba(0,0,0,0.16)',
        }}
      >
        <div style={{ padding: 14, borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
          <div style={{ fontWeight: 900 }}>
            {props.title || 'Add logistics'}
            <span style={{ marginLeft: 10, fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
              {selectedIds.length} selected
            </span>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button className="mini-btn" type="button" onClick={clear} disabled={selectedIds.length === 0}>
              Clear
            </button>
            <button className="mini-btn" type="button" onClick={onClose}>
              Close
            </button>
            <button className="btn btn-primary" type="button" onClick={onConfirm} disabled={selectedIds.length === 0}>
              Add {selectedIds.length}
            </button>
          </div>
        </div>

        <div style={{ padding: 14, borderBottom: '1px solid #f3f4f6' }}>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search logistics…"
            style={{ width: '100%' }}
          />
        </div>

        <div style={{ padding: 14, overflow: 'auto', maxHeight: 'calc(min(80vh, 720px) - 120px)' }}>
          {TYPE_ORDER.filter(t => (grouped.get(t) || []).length > 0).map((t) => {
            const items = grouped.get(t) || [];
            return (
              <div key={t} style={{ marginBottom: 14 }}>
                <div style={{ fontWeight: 900, marginBottom: 8 }}>
                  {TYPE_LABEL[t]} <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>({items.length})</span>
                </div>
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
                  {items.map((l) => {
                    const id = String(l.id || '');
                    const isChecked = selected.has(id);
                    return (
                      <button
                        key={id}
                        type="button"
                        style={{
                          display: 'flex',
                          gap: 10,
                          alignItems: 'flex-start',
                          padding: '10px 12px',
                          borderTop: '1px solid #f3f4f6',
                          borderLeft: 'none',
                          borderRight: 'none',
                          borderBottom: 'none',
                          width: '100%',
                          textAlign: 'left',
                          cursor: 'pointer',
                          background: isChecked ? '#eef2ff' : 'white',
                        }}
                        onClick={() => toggle(id)}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          readOnly
                          style={{ marginTop: 2, pointerEvents: 'none' }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, color: '#111827' }}>{l.label || id}</div>
                          <div style={{ fontSize: 12, color: '#6b7280', fontFamily: 'monospace' }}>{id}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {filtered.length === 0 ? (
            <div className="muted">No logistics match your search.</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

